import React from 'react';
import { useTranslation } from 'react-i18next';
import { btn, btnPrimary, card, inp } from './ui';
import { useUnsavedGuard } from '../../utils/unsaved';
// Batch 12 tail T2 (FS-2, TL-10): the one Esc stack and the one body lock, shared with MerchantDoc/shell.tsx's DocWindow.
import { EscDepthCtx, useEscLayer } from '../../utils/escLayer';
import { lockBodyScroll } from '../../utils/bodyLock';

// ── Dialog chrome shared by the writing panes (owner 2026-10-13) ────────────────────────────────
// Batch 2 (Banking) wrote the first dialogs of the workspace as fixed overlays with inline styles —
// there is no shared Modal in the repo. Batch 3 (Purchases) needs the same shapes, so they live here
// once instead of being copied pane by pane: the overlay dialog (a right-hand drawer for a record's detail
// stood here until batch 12 tail T2 made its one caller a full-screen window), the field/label/button styles the forms
// use, the filter chip the shell's submenu row wears
// (index.tsx keeps its own private), a loader and the stat card. Nothing here fetches or computes.

export const r2 = (n: number) => Math.round(n * 100) / 100;
export const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
export const isoDate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));

export const chip: React.CSSProperties = {
  padding: '5px 11px', fontSize: 12, fontWeight: 600, borderRadius: 999, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer',
};
export const chipOn: React.CSSProperties = { background: 'var(--accent-bg)', color: 'var(--accent-text)', border: '1px solid var(--accent)' };
export const actBtn: React.CSSProperties = { ...btn, padding: '4px 9px', fontSize: 12 };
export const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', display: 'block', marginBottom: 5 };
export const field: React.CSSProperties = { ...inp, width: '100%' };
export const cancelBtn: React.CSSProperties = { ...btn, background: 'var(--c-gray-100)', color: 'var(--text-secondary)', border: 'none' };
export const emptyLine: React.CSSProperties = { padding: 26, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 };
export const Loader = () => <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>;
export const Stat = ({ label, value, tone, sub }: { label: string; value: React.ReactNode; tone?: string; sub?: string }) => (
  <div style={{ ...card, minWidth: 150, flex: '1 1 150px' }}>
    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 800, color: tone || 'var(--text-primary)', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {sub && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>{sub}</div>}
  </div>
);

// ── FS-14 / FS-15: what "full screen" is measured against, decided so it is not re-litigated ──────
// FS-14 asked which box a full-screen document window fills: the workspace's content area (inside
// the shell's menu rail) or the browser viewport. The question does not actually arise, and that is
// a property of the code rather than a preference: Dialog has ALWAYS been `position: fixed; inset: 0`
// with a backdrop over the whole viewport, so a dialog has never been contained by the workspace
// shell. `fullscreen` only grows the panel inside that same overlay. So: FULL SCREEN MEANS THE
// BROWSER VIEWPORT, and the menu rail is covered like everything else.
// FS-15 / FS-2: In Sales, Purchases, Expenses (Reimbursements included) and Banking, every window a pane opens is a
// full-screen `Dialog` with its Save / Cancel in `footer=` and no `width`, carrying the document number in `docNo` where
// one exists — reason-only and one-choice windows included. The two shared cross-module cards (`DocEntriesDialog`,
// `ReverseReasonDialog`) stay cards (TL-9). Native `window.confirm` prompts stay native.
// (Batch 12 tail T2, TL-8: this replaces the note that Banking's windows would move to full screen later, one at a time —
// they are Dialog call sites like the rest, so they changed with the rest. There is no second shell and no Banking special case —
// two shells would drift, which is the whole reason this file exists. The 14 small cards left are Payroll's own, which
// FS-2 / FS-15 do not cover.)

/** FS-10's body lock: utils/bodyLock.ts (counted — see there), re-exported so this file stays the one import the panes
 *  and testFullscreenShell use. MerchantDoc/shell.tsx's full-screen DocWindow takes the same lock. */
export { lockBodyScroll };

/** FS-4, the dirty-check contract, offered to the call sites rather than imposed on them.
 *  The house precedent is Admin/JobRequiredDocs.tsx: the owner of the form keeps a `dirty` flag and
 *  guards close with `window.confirm(t('rqd_discard'))`. Same idiom here, moved up into the shell so
 *  the FOUR exits cannot drift apart — and they do drift: the backdrop click is the one everybody
 *  forgets, and it is the easiest way to lose somebody's typing.
 *  A caller that passes nothing gets exactly today's behaviour. */
function useCloseGate(onClose: () => void, busy?: boolean, dirty?: boolean) {
  const { t } = useTranslation();
  return React.useCallback(() => {
    if (busy) return;                                                   // a request is in flight
    if (dirty && !window.confirm(t('aw_dlg_discard'))) return;          // asked once, on every exit
    onClose();
  }, [busy, dirty, onClose, t]);
}

/** The gated closer, for the Save/Cancel the CALLER renders. Cancel is a button inside `children`,
 *  so the shell cannot intercept it — it has to be handed over, or that one exit would skip the ask
 *  (or grow a second confirm of its own). Foot below reads it, which is why every existing Cancel
 *  becomes gated without the panes being touched. Outside a Dialog the hook returns its fallback. */
const CloseGateCtx = React.createContext<(() => void) | null>(null);
export const useDialogClose = (fallback?: () => void): (() => void) => {
  const gated = React.useContext(CloseGateCtx);
  return gated || fallback || (() => {});
};

/** Fixed overlay + card, the repo's dialog shape (no shared Modal exists). zIndex above the Fab
 *  (100) so the dialog covers it.
 *
 *  ONE component, two sizes. `fullscreen` is the FS-1 document window — title and document number
 *  top left, a prominent X top right, Save/Cancel pinned at the bottom, content scrolling by itself
 *  under them so Save stays reachable without scrolling to the end of a long form. Everything is
 *  opt-in per call site: a caller that passes none of the new props keeps the 520px card (FS-15 / FS-2
 *  above says which windows must opt in).
 *
 *  Exits: the X, Esc, the backdrop, and the caller's own Cancel (via useDialogClose / Foot). All
 *  four run the same gate — so closing never saves, and never discards silently. */
export function Dialog({ title, docNo, onClose, busy, width = 520, fullscreen, dirty, footer, children }: {
  title: React.ReactNode;
  /** FS-1: the document's number, beside the title at the top left. */
  docNo?: React.ReactNode;
  onClose: () => void;
  busy?: boolean;
  width?: number;
  /** FS-1: fill the viewport instead of the 520px card. Opt-in, per call site. */
  fullscreen?: boolean;
  /** FS-4: the caller has unsaved work, so every exit asks first. Opt-in, per call site. */
  dirty?: boolean;
  /** FS-1: Save/Cancel, pinned below the scrolling content. Opt-in; callers that render their
   *  buttons as the last child of `children` keep working unchanged. */
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const close = useCloseGate(onClose, busy, dirty);
  // The fifth, sixth and seventh exits — the browser's Back, an in-app jump, and the pane being
  // unmounted from above — are not this window's to see. It registers its `dirty` with the page-wide
  // registry, which guards them with the same question (utils/unsaved.ts); `busy` rides along so a
  // dirty window's save in flight holds them too (a clean window's save registers nothing).
  useUnsavedGuard(!!dirty, !!busy);

  // Esc, for the small dialog as well as the full-screen one: a window that cannot be closed by Esc
  // when its neighbour can is its own small cruelty. Registered once per window on the ONE Esc stack
  // both shells share (utils/escLayer.ts — why depth and not mount order is written there), at its
  // own nesting depth; the handler reads `close` through a ref, so it can never be holding a stale
  // `dirty`. A MerchantDoc DocWindow counts in the same depth, so a Dialog opened inside one (the
  // in-books "Reverse JV-…" panel over a Sales window) is the deeper of the two and answers alone.
  const depth = React.useContext(EscDepthCtx) + 1;
  useEscLayer(close, depth);

  // FS-10. The full-screen window covers the page, so the page behind must not scroll under it and
  // must be where it was when the window closes. The small dialog is left alone: its page is still
  // visible around the card (only Payroll's own cards and the two shared panels are small now).
  React.useEffect(() => {
    if (!fullscreen) return;
    return lockBodyScroll();
  }, [fullscreen]);

  const head = (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 'none' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: fullscreen ? 17 : 16, fontWeight: 800, color: 'var(--text-primary)' }}>{title}</div>
        {docNo ? <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{docNo}</div> : null}
      </div>
      <button type="button" onClick={close} disabled={busy} aria-label={t('aw_dlg_close')} title={t('aw_dlg_close')}
        style={{ ...actBtn, padding: fullscreen ? '6px 10px' : '4px 7px', fontSize: fullscreen ? 16 : 13, opacity: busy ? 0.5 : 1, flex: 'none' }}>
        <i className="ti ti-x" aria-hidden />
      </button>
    </div>
  );

  // The panel. Full screen fills the viewport; otherwise it is the card as it has always been. Both
  // are a flex column, so a `footer` is pinned and the content between scrolls on its own.
  // Full screen stays IN FLOW and simply fills the overlay (width/height 100%) rather than going
  // position:absolute inset:0 — an out-of-flow child inside the overlay's flex centring is one more
  // interaction to reason about for no gain, and `width` is ignored here by design.
  const panel: React.CSSProperties = fullscreen
    ? { width: '100%', height: '100%', maxWidth: 'none', background: 'var(--bg-surface)', borderRadius: 0, padding: 22, boxShadow: 'none', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 12 }
    : { width, maxWidth: '92vw', maxHeight: '90vh', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-md)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 12 };

  return (
    <CloseGateCtx.Provider value={close}>
      {/* A window opened from inside this one is one level deeper, which is how Esc knows which of
          the two to answer without depending on the order React ran their effects in. */}
      <EscDepthCtx.Provider value={depth}>
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onMouseDown={e => { if (e.target === e.currentTarget) close(); }}>
          <div style={panel}>
            {head}
            {/* minHeight 0 is what actually lets a flex child scroll instead of growing the panel. */}
            <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>{children}</div>
            {footer ? <div style={{ flex: 'none', paddingTop: 12, borderTop: '1px solid var(--border)' }}>{footer}</div> : null}
          </div>
        </div>
      </EscDepthCtx.Provider>
    </CloseGateCtx.Provider>
  );
}

/** A dialog's Save/Cancel row. Folded up out of panes/Expenses.tsx and panes/Purchases.tsx, which
 *  held byte-identical nine-line copies of it. Cancel routes through the shell's close gate when
 *  there is one, so the dirty check covers it without the panes repeating the confirm. */
export type TF = (key: string, opts?: Record<string, unknown>) => string;
export const Foot = ({ onClose, onSubmit, busy, ready, icon, label, t }: { onClose: () => void; onSubmit: () => void; busy: boolean; ready: boolean; icon: string; label: string; t: TF }) => {
  const close = useDialogClose(onClose);
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      <button onClick={close} disabled={busy} style={cancelBtn}>{t('aw_bank_cancel')}</button>
      <button onClick={onSubmit} disabled={!ready || busy} style={{ ...btnPrimary, opacity: !ready || busy ? 0.5 : 1 }}>
        <i className={busy ? 'ti ti-loader' : icon} aria-hidden /> {label}
      </button>
    </div>
  );
};

// (Batch 12 tail T2, task 5) The right-hand `Drawer` that stood here is gone: its one caller, the reimbursement claim's
// detail, became the full-screen ClaimWindow (panes/Expenses.tsx), and a drawer answered no Esc. 0 callers at removal.
